import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import { AppShell } from '@/components/shell/AppShell';
import { AudioReportPanel } from '@/components/lesson-packs/AudioReportPanel';
import { PresentMode } from '@/components/teach/PresentMode';
import { TeachStudio } from '@/components/teach/TeachStudio';
import { MainWorkspace } from '@/components/workspace/MainWorkspace';
import { api } from '@/lib/api';
import { useEditorHotkeys } from '@/lib/keymap';
import type { LessonPack, Script } from '@/lib/schema.types';
import { useEditorStore } from '@/store/editor.store';


export default function EditLessonPackPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const previewing = searchParams.get('preview') === '1';
  const { data, error, isLoading } = useSWR(
    id ? ['/lesson-packs', id, 'full'] : null,
    () => api.lessonPacks.get(id),
    { revalidateOnFocus: false },
  );

  useEffect(() => {
    if (data) {
      useEditorStore.getState().hydrate(packToScript(data), 'pack', {
        entryBeatId: data.entryBeatId ?? null,
        publishStateAtLoad: data.publishState,
      });
    }
  }, [data]);


  useEffect(() => {
    return () => {
      void useEditorStore.getState().drainAndReset();
    };
  }, []);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      const status = useEditorStore.getState().syncStatus;
      if (status === 'pending' || status === 'saving' || status === 'error') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  useEditorHotkeys();

  if (isLoading)
    return (
      <FullScreenStatus
        icon={<Loader2 className="animate-spin" />}
        text="Loading lesson pack…"
      />
    );
  if (error)
    return (
      <FullScreenStatus
        text={`Couldn't load pack. ${error instanceof Error ? error.message : ''}`}
      />
    );
  if (!data) return null;

  if (data.status !== 'done') {
    return (
      <FullScreenStatus
        icon={<Loader2 className="animate-spin" />}
        text={`Pack is still ${data.status} — can't open the editor yet.`}
      />
    );
  }

  return (
    <>
      <AppShell>
        <MainWorkspace />
      </AppShell>
      <TeachStudioMount />
      <AudioReportPanel packId={id} />
      {previewing && (
        <PresentMode
          onClose={() => {
            const next = new URLSearchParams(searchParams);
            next.delete('preview');
            setSearchParams(next, { replace: true });
          }}
        />
      )}
    </>
  );
}


function packToScript(pack: LessonPack): Script {
  return {
    id: pack._id,
    version: String(pack.schemaVersion ?? 1),
    label: pack.title,
    metadata: {
      subject: pack.subject,
      chapter: pack.chapterReference,
      topic: pack.inputMetadata?.topic,
      classLevel: pack.grade,
    },
    slides: pack.slides,
    createdAt: pack.createdAt,
    updatedAt: pack.updatedAt,
  };
}

function TeachStudioMount() {
  const teachingSlideId = useEditorStore((s) => s.teachingSlideId);
  if (!teachingSlideId) return null;
  return (
    <TeachStudio
      slideId={teachingSlideId}
      onClose={() => useEditorStore.getState().setTeachingSlideId(null)}
    />
  );
}

function FullScreenStatus({ text, icon }: { text: string; icon?: React.ReactNode }) {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-bg text-sm text-fg-muted">
      <div className="flex items-center gap-2">
        {icon}
        <span>{text}</span>
      </div>
    </div>
  );
}
